import React from 'react';
import { LAST_UPDATED_DATE, LEGAL_TODOS } from './legalPlaceholders';

const TermsOfUse = () => (
  <main className="legal-page">
    <h1>Terms of Use</h1>
    <p>
      <strong>Last updated:</strong> {LAST_UPDATED_DATE}
    </p>
    <p>
      These Terms of Use govern access to and use of 101Setups, including any account, community, content-upload,
      image, tagging, sharing, and related functionality made available through the service.
    </p>
    <p>
      By accessing or using 101Setups, you agree to these Terms. If you do not agree, you must not use the service.
    </p>

    <h2>1. About 101Setups</h2>
    <p>
      101Setups is a desk-setup inspiration and discovery platform. It may contain editorial content, community or
      user-submitted content, and outbound links to third-party websites and sellers. Some links are affiliate links,
      including Amazon links, which means 101Setups may earn a commission if you buy through them at no extra cost to
      you.
    </p>
    <p>
      101Setups is not a marketplace and does not sell third-party products directly through the platform unless we
      expressly state otherwise.
    </p>

    <h2>2. Eligibility and accounts</h2>
    <p>
      You must provide accurate information when creating an account and keep your account credentials secure. You are
      responsible for activity carried out through your account.
    </p>
    <p>
      You must not impersonate another person, create an account for unlawful purposes, or attempt to access accounts,
      systems, or data that do not belong to you.
    </p>
    <p>
      If you are under 14, you must not create an account or submit personal data without the authorisation of your
      parent or legal guardian.
    </p>

    <h2>3. User content</h2>
    <p>
      If you upload, submit, publish, or share images, setup details, tags, descriptions, comments, profile
      information, or other material through 101Setups (&quot;User Content&quot;):
    </p>
    <ul>
      <li>
        you confirm that you own the content or have all rights, permissions, and consents necessary to submit it and
        authorise its use under these Terms;
      </li>
      <li>you remain responsible for the legality, accuracy, and reliability of your User Content;</li>
      <li>
        you must ensure that your User Content does not infringe copyright, trade marks, database rights, image rights,
        privacy rights, publicity rights, or any other third-party rights; and
      </li>
      <li>
        you must ensure that your User Content is not unlawful, misleading, defamatory, hateful, obscene, abusive,
        invasive of privacy, or otherwise prohibited.
      </li>
    </ul>

    <h2>4. Licence you grant to 101Setups</h2>
    <p>
      By submitting User Content to 101Setups, you grant us a non-exclusive, worldwide, royalty-free licence to host,
      store, reproduce, format, adapt for technical purposes, display, publish, distribute, and make that User Content
      available through the service and related promotional materials for the purpose of operating, promoting,
      improving, and protecting 101Setups.
    </p>
    <p>
      This licence lasts for as long as the User Content remains on the service and for a reasonable backup, archival,
      compliance, and dispute-resolution period afterwards. This licence does not transfer ownership of your User
      Content to us.
    </p>

    <h2>5. Prohibited conduct</h2>
    <p>You must not:</p>
    <ul>
      <li>upload or publish content that is unlawful or infringes the rights of others;</li>
      <li>use the service to harass, threaten, deceive, or abuse others;</li>
      <li>submit malware, malicious code, or harmful scripts;</li>
      <li>scrape, copy, or extract data from the service in an abusive or unauthorised way;</li>
      <li>attempt to bypass security, rate limits, or access controls;</li>
      <li>use the service to promote counterfeit, fraudulent, or illegal products or services; or</li>
      <li>use the service in a way that could damage, disable, or impair the platform.</li>
    </ul>

    <h2>6. Moderation and enforcement</h2>
    <p>
      We may review, moderate, refuse, restrict, suspend, remove, or disable access to any User Content or account if
      we reasonably believe it violates these Terms, infringes rights, creates legal or reputational risk, threatens
      security, or is otherwise inappropriate for the platform.
    </p>
    <p>
      We may also preserve or disclose information where reasonably necessary to investigate abuse, enforce these Terms,
      comply with law, or respond to lawful requests.
    </p>

    <h2>7. Notice-and-takedown</h2>
    <p>
      If you believe content on 101Setups infringes your rights or is unlawful, please contact us at
      {' '}
      {LEGAL_TODOS.legalContactEmail} with enough information for us to identify the content, understand the complaint,
      and review the request.
    </p>

    <h2>8. Intellectual property in the service</h2>
    <p>
      101Setups, its branding, site design, compilation, and original materials created by us are protected by
      intellectual property rights owned by or licensed to the operator. Except as permitted by law, you may not copy,
      reproduce, distribute, adapt, reverse engineer, or commercially exploit any part of the service without prior
      written permission.
    </p>

    <h2>9. Third-party links and product information</h2>
    <p>
      101Setups may link to third-party websites, marketplaces, or social platforms. Product links may lead to search
      results, listing pages, or other third-party pages and may not always point to a verified exact product match
      unless expressly stated.
    </p>
    <p>
      We do not control third-party sites and are not responsible for their content, availability, prices, stock,
      product descriptions, fulfilment, warranties, or privacy practices. Any purchase or interaction you make with a
      third party is solely between you and that third party.
    </p>

    <h2>10. No professional advice</h2>
    <p>
      Content on 101Setups is provided for general information, inspiration, and discovery only. It does not constitute
      professional, technical, ergonomic, medical, legal, financial, or tax advice.
    </p>

    <h2>11. Availability and changes</h2>
    <p>
      We may modify, suspend, withdraw, or discontinue all or part of the service at any time, with or without notice,
      to the extent permitted by law. We do not guarantee that the service will always be available, uninterrupted,
      secure, or error-free.
    </p>

    <h2>12. Liability</h2>
    <p>
      To the maximum extent permitted by law, 101Setups shall not be liable for indirect, incidental, special,
      consequential, or punitive damages, or for loss of profits, revenue, data, goodwill, or business opportunities
      arising from or related to your use of the service.
    </p>
    <p>
      Nothing in these Terms excludes or limits liability for fraud, wilful misconduct, personal injury, or any
      liability that cannot be excluded or limited under applicable law.
    </p>

    <h2>13. Termination</h2>
    <p>
      You may stop using the service at any time. We may suspend or terminate access to the service or your account if
      you breach these Terms, create legal or security risk, or misuse the platform.
    </p>

    <h2>14. Privacy</h2>
    <p>Use of 101Setups is also subject to our Privacy Policy and Cookie Policy.</p>

    <h2>15. Applicable law and jurisdiction</h2>
    <p>
      These Terms are governed by Spanish law. Unless mandatory consumer or other applicable law provides otherwise,
      disputes relating to these Terms or the use of 101Setups shall be submitted to the courts of
      {' '}
      {LEGAL_TODOS.jurisdictionCity}, Spain.
    </p>

    <h2>16. Contact</h2>
    <p>For legal notices, rights complaints, or questions about these Terms, contact {LEGAL_TODOS.legalContactEmail}.</p>
  </main>
);

export default TermsOfUse;
